import { IComplaintTrackingRow } from 'types/ComplaintTracking';
import { ThunkApiType } from 'types/utils';
import authFetch, { checkForUnauthorizedResponse } from 'utils/axios';
import { exportListToExcel } from 'utils/download';
import { buildComplaintFilterParams } from 'utils/utils';

export const getComplaintTrackingThunk = async (
	_: void,
	thunkAPI: ThunkApiType,
) => {
	const { page } = thunkAPI.getState().complaintTracking;
	const { queryParams } = buildComplaintFilterParams(thunkAPI.getState().form);
	const { sortingFields, sortingDirections } = thunkAPI.getState().form;

	const getComplaintTrackingURL =
		`/complaints/tracking` +
		`?page=${page}&sortingFields=${sortingFields}&sortingDirections=${sortingDirections}${
			queryParams || ''
		}`;

	try {
		const response = await authFetch.get(getComplaintTrackingURL);
		return response.data;
	} catch (error: ThunkApiType) {
		return checkForUnauthorizedResponse(error, thunkAPI);
	}
};

export const getComplaintTrackingDetailThunk = async (
	{ uuid, id }: { uuid?: string; id?: string },
	thunkAPI: ThunkApiType,
) => {
	const getComplaintDetailURL = uuid
		? `/complaints/claims/${uuid}`
		: `/complaints/${id}`;

	try {
		const response = await authFetch.get(getComplaintDetailURL);
		const data = response.data;

		// BE sends `frlSent` as 0/1; normalize to boolean (0 is a valid "No", not empty).
		if (data?.frlSent !== null && data.frlSent !== undefined) {
			data.frlSent = Boolean(data.frlSent);
		}

		return data;
	} catch (error: ThunkApiType) {
		return checkForUnauthorizedResponse(error, thunkAPI);
	}
};

export const saveComplaintThunk = async (_: {}, thunkAPI: ThunkApiType) => {
	const { complaintDetail } = thunkAPI.getState().complaintTracking;
	const body = {
		...complaintDetail,
	};
	try {
		if (!complaintDetail.complaintId) {
			const response = await authFetch.post(`/complaints`, body);
			return response.data;
		} else {
			const response = await authFetch.put(`/complaints`, body);
			return response.data;
		}
	} catch (error: ThunkApiType) {
		return checkForUnauthorizedResponse(error, thunkAPI);
	}
};

export const downloadComplaintTrackingThunk = async (
	{ uuid, type }: { uuid?: string; type: 'PDF' | 'XLSX' },
	thunkAPI: ThunkApiType,
) => {
	let getDownloadClaimIdURL = '/complaint/downloadComplaintDetail';
	let getDownloadClaimsListURL = `/complaint/downloadComplaintList`;

	const { downloadAll, selectedItems } = thunkAPI.getState().tableSelectedItems;

	if (uuid) {
		getDownloadClaimIdURL = getDownloadClaimIdURL + `?claimUuid=${uuid}`;
		try {
			const response = await authFetch.get(getDownloadClaimIdURL, {
				responseType: 'blob',
			});
			exportListToExcel(response.data, 'Claim', type);
			return true;
		} catch (error: ThunkApiType) {
			return checkForUnauthorizedResponse(error, thunkAPI);
		}
	} else {
		const { sortingFields, sortingDirections } = thunkAPI.getState().form;
		const { body: filterBody } = buildComplaintFilterParams(
			thunkAPI.getState().form,
		);

		const sortField = sortingFields || 'serviceDate,externalClaimId';
		const sortDirection = sortingDirections || 'DESC,DESC';

		const values = {
			...filterBody,
			format: type,
			complaintIds: downloadAll
				? []
				: selectedItems.map((el: IComplaintTrackingRow) => el),
			excludeUuids: !downloadAll
				? selectedItems.map((el: IComplaintTrackingRow) => el)
				: [],
			sortingFields: sortField,
			sortingDirections: sortDirection,
		};

		try {
			const response = await authFetch.post(getDownloadClaimsListURL, values, {
				responseType: 'blob',
			});
			exportListToExcel(response.data, 'Complaints', type);
			return true;
		} catch (error: ThunkApiType) {
			return checkForUnauthorizedResponse(error, thunkAPI);
		}
	}
};

export const getComplaintReasonsThunk = async (
	_: string,
	thunkAPI: ThunkApiType,
) => {
	const getComplaintReasonsURL = '/complaints/reasons';

	try {
		const response = await authFetch.get(getComplaintReasonsURL);
		return response.data;
	} catch (error: ThunkApiType) {
		return checkForUnauthorizedResponse(error, thunkAPI);
	}
};

export const getComplaintOutcomesThunk = async (
	_: string,
	thunkAPI: ThunkApiType,
) => {
	const getComplaintOutcomesURL = '/complaints/outcomes';

	try {
		const response = await authFetch.get(getComplaintOutcomesURL);
		return response.data;
	} catch (error: ThunkApiType) {
		return checkForUnauthorizedResponse(error, thunkAPI);
	}
};
